import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { fetchUsers } from '../../utils/userApi';

const NOTICES_API = 'https://system.sales-management.cloud/api/notices.php';
const ALL_USERS_DEPARTMENT_VALUE = '__all__';

const initialFormData = {
  deliveryType: 'department',
  targetUserId: '',
  targetDepartment: ALL_USERS_DEPARTMENT_VALUE,
  title: 'お知らせ',
  message: '',
  expiresAt: '',
  isActive: true,
  pushOnly: false,
};

function NoticeManagement() {
  const [users, setUsers] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(true);
  const [formData, setFormData] = useState(initialFormData);

  const departments = useMemo(() => {
    const set = new Set();
    users.forEach((user) => {
      if (user.department) {
        set.add(user.department);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [users]);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const result = await fetchUsers({
        limit: 200,
        sort_by: 'name',
        sort_order: 'asc',
      });

      if (result.status === 'success' && result.data?.users) {
        setUsers(result.data.users);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadNotices = useCallback(async (includeInactive = true) => {
    try {
      setListLoading(true);
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const query = includeInactive ? '1' : '0';
      const response = await fetch(`${NOTICES_API}?scope=admin&limit=100&include_inactive=${query}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.status === 'success') {
        setNotices(result.data?.notices || []);
      } else {
        setError(result.message || 'お知らせ一覧の取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch notices:', err);
      setError('お知らせ一覧の取得に失敗しました');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadNotices(showInactive);
  }, [showInactive, loadNotices]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'deliveryType' && value === 'user') {
        next.targetDepartment = '';
      }
      if (name === 'deliveryType' && value === 'department') {
        next.targetUserId = '';
      }
      return next;
    });
  };

  const validateForm = () => {
    if (formData.deliveryType === 'user' && !formData.targetUserId) {
      setError('送信先ユーザーを選択してください');
      return false;
    }

    if (formData.deliveryType === 'department' && !formData.targetDepartment) {
      setError('送信先部署を選択してください');
      return false;
    }

    if (!formData.message.trim()) {
      setError(formData.pushOnly ? 'プッシュ通知の本文を入力してください' : 'お知らせ本文を入力してください');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const payload = {
        title: formData.title || 'お知らせ',
        message: formData.message,
        expires_at: formData.expiresAt || null,
      };

      if (!editingId && formData.pushOnly) {
        payload.push_only = true;
      }

      if (formData.deliveryType === 'department') {
        payload.target_department = formData.targetDepartment;
        if (editingId && formData.targetUserId) {
          payload.target_user_id = Number(formData.targetUserId);
        }
      } else {
        payload.target_user_id = Number(formData.targetUserId);
        payload.target_department = null;
      }

      let method = 'POST';
      if (editingId) {
        method = 'PUT';
        payload.id = editingId;
        payload.is_active = formData.isActive;
      }

      const response = await fetch(NOTICES_API, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.status === 'success') {
        if (method === 'POST' && formData.pushOnly) {
          setSuccess(`プッシュ通知を送信しました（${result.data?.push_count ?? 0}件）`);
          resetForm();
        } else if (method === 'POST' && result.data?.delivery_type === 'department') {
          setSuccess(`部署向けお知らせを送信しました（${result.data.created_count}件）`);
          resetForm();
          await loadNotices(showInactive);
        } else {
          setSuccess(editingId ? 'お知らせを更新しました' : 'お知らせを送信しました');
          resetForm();
          await loadNotices(showInactive);
        }
      } else {
        setError(result.message || 'お知らせ処理に失敗しました');
      }
    } catch (err) {
      console.error('Notice save error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notice) => {
    setError('');
    setSuccess('');
    setEditingId(notice.id);
    setFormData({
      deliveryType: notice.delivery_type === 'department' ? 'department' : 'user',
      targetUserId: String(notice.target_user_id || ''),
      targetDepartment: notice.target_department || '',
      title: notice.title || 'お知らせ',
      message: notice.message || '',
      expiresAt: toInputDateTime(notice.expires_at),
      isActive: !!notice.is_active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleActive = async (notice) => {
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(NOTICES_API, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: notice.id,
          is_active: !notice.is_active,
        }),
      });

      const result = await response.json();
      if (result.status === 'success') {
        setSuccess(notice.is_active ? 'お知らせを停止しました' : 'お知らせを再開しました');
        await loadNotices(showInactive);
      } else {
        setError(result.message || 'ステータス更新に失敗しました');
      }
    } catch (err) {
      console.error('Toggle active error:', err);
      setError('通信エラーが発生しました');
    }
  };

  const handleDelete = async (noticeId) => {
    if (!window.confirm('このお知らせを削除しますか？')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(`${NOTICES_API}?id=${noticeId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.status === 'success') {
        setSuccess('お知らせを削除しました');
        if (editingId === noticeId) {
          resetForm();
        }
        await loadNotices(showInactive);
      } else {
        setError(result.message || '削除に失敗しました');
      }
    } catch (err) {
      console.error('Delete notice error:', err);
      setError('通信エラーが発生しました');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">お知らせ配信</h1>
        <p className="text-sm text-gray-600 mt-2">ユーザー指定・部署指定で配信できます。</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 md:p-6 space-y-4">
        {!editingId && (
          <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
            <input
              type="checkbox"
              id="pushOnly"
              name="pushOnly"
              checked={formData.pushOnly}
              onChange={handleChange}
              disabled={loading}
              className="w-4 h-4 text-indigo-600"
            />
            <label htmlFor="pushOnly" className="text-sm text-indigo-800 font-medium cursor-pointer">
              プッシュ通知のみ送信（掲示板に投稿しない）
            </label>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">配信先種別</label>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="deliveryType"
                value="user"
                checked={formData.deliveryType === 'user'}
                onChange={handleChange}
                disabled={loading}
              />
              ユーザー指定
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="deliveryType"
                value="department"
                checked={formData.deliveryType === 'department'}
                onChange={handleChange}
                disabled={loading}
              />
              部署指定
            </label>
          </div>
        </div>

        {formData.deliveryType === 'user' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">送信先ユーザー</label>
            <select
              name="targetUserId"
              value={formData.targetUserId}
              onChange={handleChange}
              disabled={usersLoading || loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100"
            >
              <option value="">ユーザーを選択してください</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">送信先部署</label>
            <select
              name="targetDepartment"
              value={formData.targetDepartment}
              onChange={handleChange}
              disabled={usersLoading || loading}
              className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100"
            >
              <option value="">部署を選択してください</option>
              <option value={ALL_USERS_DEPARTMENT_VALUE}>全員に配信</option>
              {departments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">選択した部署、または全員に配信します。</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">タイトル</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            disabled={loading}
            maxLength={255}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">本文</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            disabled={loading}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            placeholder="お知らせ内容を入力してください"
          />
        </div>

        {(!formData.pushOnly || editingId) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">有効期限（任意）</label>
            <input
              type="datetime-local"
              name="expiresAt"
              value={formData.expiresAt}
              onChange={handleChange}
              disabled={loading}
              className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">未指定の場合は無期限です。</p>
          </div>
        )}

        {editingId && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              disabled={loading}
            />
            このお知らせを有効にする
          </label>
        )}

        <div className="pt-2 flex items-center gap-2">
          <button
            type="submit"
            disabled={loading || usersLoading}
            className={`px-5 py-2 text-white rounded-md transition-colors text-sm disabled:opacity-60 ${
              formData.pushOnly && !editingId
                ? 'bg-indigo-600 hover:bg-indigo-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading
              ? '処理中...'
              : editingId
                ? '更新する'
                : formData.pushOnly
                  ? 'プッシュ通知を送信'
                  : '送信'}
          </button>

          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
            >
              編集をキャンセル
            </button>
          )}
        </div>
      </form>

      <div className="mt-8 bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-gray-900">配信中/配信履歴</h2>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            停止・期限切れも表示
          </label>
        </div>

        {listLoading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : notices.length === 0 ? (
          <p className="text-sm text-gray-500">該当するお知らせはありません</p>
        ) : (
          <div className="space-y-3">
            {notices.map((notice) => (
              <div key={notice.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">{notice.title}</p>
                      <DeliveryTypeBadge type={notice.delivery_type} />
                      <StatusBadge status={notice.delivery_status} />
                    </div>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{notice.message}</p>
                    <p className="text-xs text-gray-600 mt-2">
                      配信先: {notice.target_user_name}（ID: {notice.target_user_id}）
                      {notice.target_department
                        ? ` / ${notice.target_department === ALL_USERS_DEPARTMENT_VALUE ? '全員配信' : `部署: ${notice.target_department}`}`
                        : ''}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      有効期限: {notice.expires_at ? formatDateTime(notice.expires_at) : '無期限'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      配信者: {notice.created_by_name || '-'} / 作成: {formatDateTime(notice.created_at)} / 更新: {formatDateTime(notice.updated_at)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleEdit(notice)}
                      className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(notice)}
                      className="px-3 py-1.5 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                    >
                      {notice.is_active ? '停止' : '再開'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(notice.id)}
                      className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliveryTypeBadge({ type }) {
  if (type === 'department') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">部署配信</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">個別配信</span>;
}

function StatusBadge({ status }) {
  if (status === 'active') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">配信中</span>;
  }
  if (status === 'expired') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">期限切れ</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">停止中</span>;
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString('ja-JP');
}

function toInputDateTime(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

export default NoticeManagement;
